import { useState, useEffect, useRef } from 'react'
import AskOrellaChip from './AskOrellaChip'
import { ArrowLeft, Stethoscope } from 'lucide-react'
import { useI18n } from '../i18n'
import { useCaseStore } from '../stores'
import { useQuestionnaireTranslation } from '../i18n/questionnaires/useQuestionnaireTranslation'
import { getSpecialtyIcon } from '../utils/specialtyIcons'
import { resolveSpecialtyId, formatCaseSpecialty } from '../utils/specialtyResolver'
import { caseForTarget, forgetCaseFor, caseForInitials } from '../utils/caseFor' // s110 b77 (mockup S4)
import NotificationBellButton from './NotificationBellButton'
import orellaLogo from '../assets/logos/orella_46A8F9_logo.png'
import orellaOIcon from '../assets/logos/orella_o_icon.png'
// ★★★ Build 35 — `DIALOGUE_PAD` WAS USED ON LINE 104 AND IMPORTED NOWHERE, since
//   2026-08-16. `vite build` succeeded (Rollup treats an unresolved name as a global,
//   minified `DIALOGUE_BUBBLE` to `ki` and emitted `DIALOGUE_PAD` verbatim), so Builds
//   30-34 shipped a ReferenceError that fired on every Deliver screen rendering
//   `SmallOrellaBubble`. `scripts/check-free-identifiers.mjs` is the gate.
import { DIALOGUE_BUBBLE, DIALOGUE_PAD } from './agent/dialogueBubble'


// Typing dots
function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1 h-5">
      <span className="w-1.5 h-1.5 rounded-full bg-[#A7D9FC] animate-[dotPulse_1.2s_ease-in-out_infinite]" />
      <span className="w-1.5 h-1.5 rounded-full bg-[#A7D9FC] animate-[dotPulse_1.2s_ease-in-out_infinite_0.2s]" />
      <span className="w-1.5 h-1.5 rounded-full bg-[#A7D9FC] animate-[dotPulse_1.2s_ease-in-out_infinite_0.4s]" />
    </span>
  )
}

// Shared keyframes style block
export function ChatKeyframes() {
  return (
    <style>{`
      @keyframes dotPulse {
        0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
        40% { opacity: 1; transform: scale(1.1); }
      }
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes fadeSlideUp {
        from { opacity: 0; transform: translateY(16px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `}</style>
  )
}

// ============================================
// ANIMATED CARD — staggered fade+slide wrapper
// Used for sequential reveal of dialogue cards
// ============================================
export function AnimatedCard({ visible, delay = 0, children, className = '' }) {
  const [show, setShow] = useState(false)
  useEffect(() => {
    if (visible) {
      const t = setTimeout(() => setShow(true), delay)
      return () => clearTimeout(t)
    }
  }, [visible, delay])

  return (
    <div
      className={`px-4 mb-2 transition-all duration-500 ${className}`}
      style={{
        transitionTimingFunction: 'cubic-bezier(0.16,1,0.3,1)',
        opacity: show ? 1 : 0,
        transform: show ? 'translateY(0)' : 'translateY(16px)',
      }}
    >
      {children}
    </div>
  )
}

// ============================================
// SMALL ORELLA BUBBLE — inline chat intro for cards
// Smaller, no sessionStorage replay, just delay-based
// ============================================
export function SmallOrellaBubble({ message, visible, delay = 0 }) {
  const [showLogo, setShowLogo] = useState(false)
  const [showBubble, setShowBubble] = useState(false)
  const [showDots, setShowDots] = useState(false)
  const [showText, setShowText] = useState(false)

  useEffect(() => {
    if (!visible) return
    const t = []
    t.push(setTimeout(() => setShowLogo(true), delay + 100))
    t.push(setTimeout(() => { setShowBubble(true); setShowDots(true) }, delay + 200))
    t.push(setTimeout(() => { setShowDots(false); setShowText(true) }, delay + 700))
    return () => t.forEach(clearTimeout)
  }, [visible, delay])

  if (!visible) return null

  return (
    <div className="px-4 pt-3 pb-1">
      <div className="flex items-start gap-2.5">
        <img
          src={orellaOIcon}
          alt="O"
          className="w-9 h-9 rounded-lg flex-shrink-0 object-contain transition-all duration-300"
          style={{
            opacity: showLogo ? 1 : 0,
            transform: showLogo ? 'scale(1)' : 'scale(0.75)',
          }}
        />
        <div
          className={`flex-1 ${DIALOGUE_BUBBLE} ${DIALOGUE_PAD} transition-all duration-300`}
          style={{
            opacity: showBubble ? 1 : 0,
            transform: showBubble ? 'translateY(0) scale(1)' : 'translateY(4px) scale(0.95)',
          }}
        >
          {showDots && !showText && <TypingDots />}
          {showText && (
            <p className="text-[15.5px] font-medium text-[#15354F] leading-relaxed animate-[fadeIn_0.3s_ease]">
              {message}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

// Compact header with breadcrumb + 4D progress bar
// ★★ v242.235.0 (session 96, mockup B ruling) — THE CASE HEADER IS THE ORELLA HEADER, AND IT
//   CARRIES NO TRACKER. Dr. Rami: "remove the describe detect share tracker at the top - keep
//   orella header style". The "Step x of 7" breadcrumb and the 4D progress bar are gone from
//   every case screen at once (this is the one component they all render). Row 1 stays the
//   chrome (back, wordmark, status, bell); row 2 is AppHeader's tinted band with the case's
//   area icon, area name and focus-area chip — the same band Home uses.
//   The props stepNum / totalSteps / stepLabel are still ACCEPTED so no caller has to change,
//   and deliberately not rendered. caseInfo is the title only when there is no current case.
export function CompactHeader({
  stepNum, // eslint-disable-line no-unused-vars
  totalSteps = 4, // eslint-disable-line no-unused-vars
  stepLabel, // eslint-disable-line no-unused-vars
  caseInfo,
  statusLabel,
  onBack,
  onNotification
}) {
  const { t } = useI18n()
  const { tfa } = useQuestionnaireTranslation()
  const currentCase = useCaseStore((st) => st.currentCase)
  const areaId = currentCase ? resolveSpecialtyId(currentCase.specialtyId || currentCase.specialty?.id || currentCase.specialtyName) : null
  const icon = currentCase ? getSpecialtyIcon({ id: areaId, name: currentCase.specialtyName }) : null
  const title = currentCase ? formatCaseSpecialty(currentCase, t) : (caseInfo || '')
  const focusRaw = currentCase ? (currentCase.focusAreaName || currentCase.focusArea?.name || '') : ''
  const focus = focusRaw ? tfa(focusRaw) : ''
  // ★ s110 (b77, mockup S4): a case started from a doctor says who it is for while it is being built.
  const [forDismissed, setForDismissed] = useState(false)
  const caseFor = forDismissed ? null : caseForTarget(currentCase && currentCase.id)
  // PROD-1417: rows 1-3 stick together below the iOS status bar (.sticky-page-header, index.css),
  //   on every case screen at once. Held by check-sticky-header.
  return (
    <div className="sticky-page-header flex-shrink-0 bg-white">
      {/* row 1 — chrome */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2.5">
        <div className="flex items-center gap-2.5">
          <button onClick={onBack} className="p-1.5 -ml-1.5 text-orella-700" aria-label={t('common.back', 'Back')}>
            <ArrowLeft size={22} />
          </button>
          <img src={orellaLogo} alt="Orella" className="h-5 object-contain" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold text-green-600 bg-green-100 px-2 py-0.5 rounded-md">
            {statusLabel || t('cases.statusInProgress')}
          </span>
          {onNotification && (
            <NotificationBellButton variant="compact" onClick={onNotification} />
          )}
        </div>
      </div>

      {/* row 2 — the Orella band: area icon, area name, focus-area chip */}
      <div
        className="flex items-center gap-3 px-4 py-3"
        style={{ borderTop: '1px solid #E9F8FF', background: 'linear-gradient(180deg, #F7FCFF 0%, #E9F8FF 100%)' }}
      >
        <span
          className="flex items-center justify-center flex-none"
          style={{ width: 40, height: 40, borderRadius: 12, background: '#fff', border: '1.5px solid #A7D9FC', boxShadow: '0 2px 6px rgba(21,53,79,0.06)' }}
        >
          {icon ? <img src={icon} alt="" style={{ width: 27, height: 27, objectFit: 'contain' }} /> : <Stethoscope size={20} style={{ color: '#46A8F9' }} />}
        </span>
        <h1 className="flex-1 min-w-0 truncate" style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-0.02em', color: '#15354F', margin: 0, lineHeight: 1.2 }}>
          {title}
        </h1>
        {focus && (
          <span
            className="flex items-center truncate"
            style={{ maxWidth: '46%', height: 26, borderRadius: 999, background: '#fff', border: '1.5px solid #A7D9FC', padding: '0 10px', fontSize: 12, fontWeight: 700, color: '#3F5F7C', whiteSpace: 'nowrap' }}
          >
            {focus}
          </span>
        )}
      </div>

      {/* row 3 (s110, mockup S4) — who this case is for */}
      {caseFor && (
        <div className="flex items-center gap-2.5 px-4 py-2" data-testid="case-for-strip" style={{ background: '#F4FBFF', borderTop: '1px solid #E9F8FF' }}>
          <span className="flex items-center justify-center flex-none font-extrabold" style={{ width: 30, height: 30, borderRadius: 999, background: '#fff', border: '1px solid #A7D9FC', color: '#46A8F9', fontSize: 11 }}>
            {caseForInitials(caseFor.name)}
          </span>
          <span className="flex-1 min-w-0 leading-tight">
            <b className="block truncate" style={{ fontSize: 13, color: '#15354F' }}>{t('caseFor.title', { defaultValue: 'For {{doctor}}', doctor: caseFor.name })}</b>
            <span className="block truncate" style={{ fontSize: 11.5, color: '#3F5F7C' }}>
              {[caseFor.specialty, caseFor.kind === 'office'
                ? t('caseFor.officeLine', 'faxed when your report is ready')
                : t('caseFor.orellaLine', 'on Orella')].filter(Boolean).join(' \u00b7 ')}
            </span>
          </span>
          <button type="button" onClick={() => { forgetCaseFor(); setForDismissed(true) }} className="flex-none font-bold" style={{ fontSize: 12.5, color: '#46A8F9' }}>
            {t('caseFor.change', 'Change')}
          </button>
        </div>
      )}
    </div>
  )
}

// Orella chat bubble with typing animation
// v242.88.0: `instant` renders the bubble fully formed and fires onComplete on
// the next tick — no logo pop, no typing dots, no 1.2 s wait.
// ★ There WAS already a skip path: the effect below short-circuits when
//   sessionStorage holds this bubble's key. That is a cache, not a decision —
//   sessionStorage is empty after every app launch, so "I have read this before"
//   was true only within a single run. A screen that KNOWS its content is
//   historical should say so rather than hope the cache is warm. (TF b34 #6)
// ★★★ v242.120.0 · ASKORELLA-CHIP-CONFORM-1 — Dr. Rami: "we need to match the Ask
//   orella pill to the other places in the app (see profile section rendering)."
//   ★★★ THE CANONICAL RENDERING IS OrellaPageHeader's: the chip lives INSIDE the
//     bubble, under the message, behind a `border-t border-[#A7D9FC]/30` hairline, in
//     the `outline` variant — white fill, 1.5px navy border, navy text and icon. The
//     Deliver screen had it floating below the bubble, centred, in the ice-blue
//     `default` variant: the same control, drawn two ways, on two screens a patient
//     sees minutes apart.
//   ★★ SO THE BLOCK IS COPIED, NOT APPROXIMATED — same classes, same divider, same
//     variant, same 'primary' escape hatch — and it lives HERE, on the shared bubble,
//     so the next screen that wants a chip gets the canonical one for free rather than
//     hand-rolling a third drawing.
export function OrellaChatBubble({ message, animationKey, onComplete, customStyle, instant = false, chips = [] }) {
  const [showLogo, setShowLogo] = useState(false)
  const [showBubble, setShowBubble] = useState(false)
  const [showDots, setShowDots] = useState(false)
  const [showText, setShowText] = useState(false)

  useEffect(() => {
    const key = `orella_chat_${animationKey}`
    const hasPlayed = instant || sessionStorage.getItem(key)
    if (hasPlayed) {
      setShowLogo(true)
      setShowBubble(true)
      setShowText(true)
      // CRITICAL: Use requestAnimationFrame + small delay so parent's setShowContent(false)
      // renders first, then our onComplete triggers the opacity 0→1 CSS transition.
      // Without this, React batches both updates and content stays invisible on back-nav.
      const t = setTimeout(() => onComplete?.(), 60)
      return () => clearTimeout(t)
    }
    const timers = []
    timers.push(setTimeout(() => setShowLogo(true), 200))
    timers.push(setTimeout(() => { setShowBubble(true); setShowDots(true) }, 400))
    timers.push(setTimeout(() => { setShowDots(false); setShowText(true); sessionStorage.setItem(key, 'true'); onComplete?.() }, 1200))
    return () => timers.forEach(clearTimeout)
  }, [animationKey, instant])

  return (
    <div className="px-4 pt-3 pb-1">
      <div className="flex items-start gap-2.5">
        <img 
          src={orellaOIcon} 
          alt="Orella" 
          className={`w-9 h-9 rounded-lg flex-shrink-0 object-contain transition-all duration-[400ms] ${showLogo ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}
        />
        <div 
          className={`flex-1 px-3.5 py-3 transition-all duration-[400ms] ${showBubble ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-1 scale-95'} ${!customStyle ? DIALOGUE_BUBBLE : 'border'}`}
          style={customStyle ? { backgroundColor: customStyle.bg, borderColor: customStyle.border } : undefined}
        >
          {showDots && !showText && <TypingDots />}
          {showText && (
            <p className="text-[15.5px] font-medium text-orella-700 leading-relaxed animate-[fadeIn_0.3s_ease]">
              {message}
            </p>
          )}
          {showText && chips.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-[#A7D9FC]/30 animate-[fadeIn_0.3s_ease]">
              {chips.map((chip) => (
                chip.variant === 'primary' ? (
                  <button
                    key={chip.label}
                    onClick={chip.onClick}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-semibold text-white active:scale-[0.96] transition-transform breathe-blue"
                    style={{ background: '#46A8F9' }}
                  >
                    {chip.label}
                  </button>
                ) : (
                  <AskOrellaChip
                    key={chip.label}
                    onClick={chip.onClick}
                    label={chip.label}
                    variant="outline"
                  />
                )
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default { ChatKeyframes, CompactHeader, OrellaChatBubble, SmallOrellaBubble, AnimatedCard }
